/**
 * src/contexts/AuthContext.tsx — the auth context and its hook. **No provider lives here.**
 *
 * Source: `D:/WCS/latex2html-french-trad/src/contexts/AuthContext.tsx`.
 * Spec: `docs/spec/06-frontend-components.md` §2.4.
 * Plan: `docs/PLAN-STACKAUTH.md` §7.1 item 4, §10 step 3.
 *
 * ═══ WHAT WAS DELETED, AND WHY THIS FILE STILL EXISTS ══════════════════════════════════════════
 * `AuthProvider` — the password-based provider — was deleted on 2026-08-09 with the rest of the
 * legacy login (owner instruction). {@link HexclaveAuthProvider} is now the only implementation.
 *
 * The **context and the hook stay here**, separate from the provider, because eleven modules import
 * `useAuth` and none of them should know or care which provider is above them: `ProtectedRoute`,
 * `ConverterApp`, `EnhancedSavedDocuments`, `Admin` and every `use-database` hook are untouched by
 * the migration precisely because this seam did not move.
 *
 * ═══ THE SHAPE SHRANK, AND THAT IS DELIBERATE ══════════════════════════════════════════════════
 * `login` and `register` are gone from {@link AuthContextType}. They were consumed by exactly one
 * component — the deleted `LegacyAuthForm` — and Hexclave's hosted pages own those flows now. The
 * previous revision kept them as stubs that threw; a method that exists only to throw is worse than
 * no method, because it still type-checks at the call site.
 *
 * `user` is the **D1** row, not Hexclave's user object: `id`, `email`, `username`, `role`,
 * `is_approved`. Hexclave knows nothing of the last two, and `id` must be the D1 integer because
 * `use-database`'s query keys and every `user_id` foreign key are built from it.
 */
import { createContext, useContext } from 'react'
import type { User } from '@/lib/types'

/**
 * Context shape — `AuthContext.tsx:5-12`, minus `login`/`register` (see the file header).
 *
 * `isLoading` starting `true` is load-bearing: it is what paints `ProtectedRoute`'s full-screen
 * spinner while `GET /api/auth/me` is in flight, rather than flashing the sign-in card at somebody
 * who is already signed in.
 */
export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** `AuthContext.tsx:16-22` — throws outside the provider, message verbatim. */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
